import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useOffice } from '@/hooks/useOffice';
import { logActivity } from '@/lib/activityLog';
import AccountModal, { type AccountUser } from './components/AccountModal';
import ResetPasswordModal from './components/ResetPasswordModal';

type ActionName =
  | 'list'
  | 'create'
  | 'rename'
  | 'set_role'
  | 'set_active'
  | 'set_offices'
  | 'reset_password'
  | 'delete';

async function call(action: ActionName, payload: Record<string, unknown> = {}) {
  const { data, error } = await supabase.functions.invoke('manage-users', {
    body: { action, ...payload },
  });
  if (error) {
    let message = error.message || 'Request failed.';
    try {
      const ctx = (error as { context?: { json?: () => Promise<unknown>; text?: () => Promise<string> } }).context;
      if (ctx?.json) {
        const j = (await ctx.json()) as { error?: string };
        if (j?.error) message = j.error;
      } else if (ctx?.text) {
        const t = await ctx.text();
        if (t) message = t;
      }
    } catch {
      /* ignore parse errors */
    }
    throw new Error(message);
  }
  return data as { users?: AccountUser[]; user?: AccountUser; ok?: boolean };
}

function formatDate(v?: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function AccountsPage() {
  const { profile } = useAuth();
  const { offices } = useOffice();
  const navigate = useNavigate();
  const [users, setUsers] = useState<AccountUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState<AccountUser | null>(null);
  const [resetUser, setResetUser] = useState<AccountUser | null>(null);
  const [deleteUser, setDeleteUser] = useState<AccountUser | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await call('list');
      setUsers(data.users ?? []);
    } catch (e: any) {
      setLoadError(e?.message || 'Failed to load accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Only admins may view account management; redirect anyone else.
  useEffect(() => {
    if (profile && profile.role !== 'admin') {
      navigate('/', { replace: true });
    }
  }, [profile, navigate]);

  const flash = (type: 'success' | 'error', text: string) => {
    setNotice({ type, text });
    window.setTimeout(() => setNotice(null), 4000);
  };

  const currentIsOwner = users.some((u) => u.id === profile?.id && u.is_owner === true);

  const handleCreate = async (values: { username: string; password?: string; role: string; office_ids: number[] }) => {
    await call('create', { username: values.username, password: values.password, role: values.role, office_ids: values.office_ids });
    setCreateOpen(false);
    await load();
    flash('success', `Account "${values.username}" created.`);
    await logActivity('create_account', `Created account "${values.username}"`);
  };

  const handleEdit = async (values: { username: string; password?: string; role: string; office_ids: number[] }) => {
    if (!editUser) return;
    await call('rename', { userId: editUser.id, username: values.username });
    await call('set_role', { userId: editUser.id, role: values.role });
    await call('set_offices', { userId: editUser.id, office_ids: values.office_ids });
    setEditUser(null);
    await load();
    flash('success', 'Account updated.');
    await logActivity('update_account', `Updated account "${values.username}"`);
  };

  const handleReset = async (password: string) => {
    if (!resetUser) return;
    await call('reset_password', { userId: resetUser.id, password });
    setResetUser(null);
    flash('success', `Password reset for "${resetUser.username}".`);
    await logActivity('reset_password', `Reset password for "${resetUser.username}"`);
  };

  const handleToggleActive = async (u: AccountUser) => {
    try {
      await call('set_active', { userId: u.id, is_active: !u.is_active });
      await load();
      flash('success', u.is_active ? `"${u.username}" deactivated.` : `"${u.username}" activated.`);
      await logActivity('set_account_status', u.is_active ? `Deactivated account "${u.username}"` : `Activated account "${u.username}"`);
    } catch (e: any) {
      flash('error', e?.message || 'Failed to update status.');
    }
  };

  const handleDelete = async () => {
    if (!deleteUser || deleting) return;
    setDeleting(true);
    try {
      await call('delete', { userId: deleteUser.id });
      setDeleteUser(null);
      await load();
      flash('success', `Account "${deleteUser.username}" deleted.`);
      await logActivity('delete_account', `Deleted account "${deleteUser.username}"`);
    } catch (e: any) {
      setDeleteUser(null);
      flash('error', e?.message || 'Failed to delete account.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Account Management</h1>
          <p className="text-sm text-slate-500 mt-1">
            Create accounts and manage names, passwords, and permissions for your team.
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="px-4 py-2 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800 cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
        >
          <i className="ri-add-line w-4 h-4 flex items-center justify-center" /> New account
        </button>
      </div>

      {notice && (
        <div
          className={`mb-4 p-3 rounded-md text-sm border flex items-center gap-2 ${
            notice.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          <i
            className={`${
              notice.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
            } w-4 h-4 flex items-center justify-center shrink-0`}
          />
          <span>{notice.text}</span>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
            <i className="ri-loader-4-line w-5 h-5 flex items-center justify-center animate-spin" />
            <span className="text-sm">Loading accounts...</span>
          </div>
        ) : loadError ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-500">
            <i className="ri-error-warning-line w-8 h-8 flex items-center justify-center text-red-400 text-2xl" />
            <p className="text-sm mt-3">{loadError}</p>
            <button
              onClick={load}
              className="mt-4 px-4 py-2 text-sm rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-refresh-line w-4 h-4 flex items-center justify-center" /> Retry
            </button>
          </div>
        ) : users.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-400">No accounts found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-100 bg-slate-50">
                <th className="px-4 py-3 font-medium">Username</th>
                <th className="px-4 py-3 font-medium">Office access</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Last sign-in</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isOwner = u.is_owner === true;
                const locked = isOwner && !currentIsOwner;
                return (
                <tr
                  key={u.id}
                  className={`border-b border-slate-100 last:border-0 ${
                    locked ? 'bg-slate-50 opacity-60' : 'hover:bg-slate-50'
                  }`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold shrink-0">
                        {u.username.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-slate-800 whitespace-nowrap">{u.username}</span>
                      {isOwner && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap font-semibold">
                          owner
                        </span>
                      )}
                      {u.id === profile?.id && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 whitespace-nowrap">you</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {u.role === 'admin' ? (
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 whitespace-nowrap">All offices</span>
                    ) : u.office_ids && u.office_ids.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {u.office_ids.map((oid) => (
                          <span key={oid} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 whitespace-nowrap">
                            {offices.find((o) => o.id === oid)?.name ?? `#${oid}`}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-100 whitespace-nowrap">No access</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                        isOwner
                          ? 'bg-amber-100 text-amber-700'
                          : u.role === 'admin'
                          ? 'bg-slate-900 text-white'
                          : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {isOwner ? 'Owner' : u.role === 'admin' ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1.5 text-xs whitespace-nowrap ${
                        u.is_active ? 'text-emerald-600' : 'text-slate-400'
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${u.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`}
                      />
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(u.last_sign_in_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setEditUser(u)}
                        disabled={locked}
                        title={locked ? 'Only the owner can edit this account' : 'Edit name & role'}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      >
                        <i className="ri-edit-line w-4 h-4 flex items-center justify-center" />
                      </button>
                      <button
                        onClick={() => setResetUser(u)}
                        disabled={locked}
                        title={locked ? 'Only the owner can reset this password' : 'Reset password'}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      >
                        <i className="ri-key-2-line w-4 h-4 flex items-center justify-center" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(u)}
                        disabled={locked}
                        title={locked ? 'Only the owner can change this status' : u.is_active ? 'Deactivate' : 'Activate'}
                        className={`w-8 h-8 flex items-center justify-center rounded-md cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed ${
                          u.is_active
                            ? 'text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:hover:bg-transparent'
                            : 'text-emerald-500 hover:bg-emerald-50 disabled:hover:bg-transparent'
                        }`}
                      >
                        <i
                          className={`${
                            u.is_active ? 'ri-indeterminate-circle-line' : 'ri-checkbox-circle-line'
                          } w-4 h-4 flex items-center justify-center`}
                        />
                      </button>
                      <button
                        onClick={() => setDeleteUser(u)}
                        disabled={u.id === profile?.id || locked}
                        title={locked ? 'Only the owner can delete this account' : u.id === profile?.id ? 'Cannot delete your own account' : 'Delete'}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      >
                        <i className="ri-delete-bin-6-line w-4 h-4 flex items-center justify-center" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <AccountModal
        open={createOpen}
        mode="create"
        user={null}
        offices={offices}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />
      <AccountModal
        open={!!editUser}
        mode="edit"
        user={editUser}
        offices={offices}
        onClose={() => setEditUser(null)}
        onSubmit={handleEdit}
      />
      <ResetPasswordModal
        open={!!resetUser}
        user={resetUser}
        onClose={() => setResetUser(null)}
        onSubmit={handleReset}
      />

      {deleteUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setDeleteUser(null)} />
          <div className="relative bg-white rounded-lg border border-slate-200 w-full max-w-sm p-6">
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-alert-line w-5 h-5 flex items-center justify-center text-red-600" />
              <h3 className="font-semibold text-base">Delete account</h3>
            </div>
            <p className="text-sm text-slate-500 mt-2 mb-5">
              Are you sure you want to delete <span className="font-medium text-slate-700">{deleteUser.username}</span>?
              This cannot be undone.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 cursor-pointer whitespace-nowrap disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <i className="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin" /> Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
              <button
                onClick={() => setDeleteUser(null)}
                className="px-4 py-2 text-sm rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}